import { Component, OnInit } from '@angular/core';
import { Utils } from '../../../../../../common/js/Utils';
import { CoverpathUtils } from '../../../../../../coverpath-common/js/static/CoverpathUtils';
import { AmorphicService } from '../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { QuickQuoteService } from '../../../services/quote/quick-quote.service';
import { TimeFrequency, Product } from '../../../../../../common/js/static/Product';
import { QuoteRider } from '../../../../../../common/js/quote/QuoteRider';
import { PolicyRider } from '../../../../../../common/js/customer/PolicyRider';
import { CoverpathControllerInterface } from '../../../../../../coverpath-common/angular/interfaces/controller/CoverpathControllerInterface';
import { CoverpathTermQuote } from '../../../../../../coverpath-common/classes/quote/coverpath/CoverpathTermQuote';
import { QuoteCardService } from './quote-card.service';
import { Controller } from '../../../Controller';
import { SafeInviteInfo } from '../../../../../../common/js/customer/SafeInviteInfo';
import { Exchange1035FundingPurpose } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { AlertIntent } from '../../../../../../coverpath-common/angular/components/shared/alert/alert.component';
import { AuthenticationService } from '../../../../../../coverpath-common/angular/services/authentication/authentication.service';

@Component({
  moduleId: module.id,
  selector: 'quote-card',
  templateUrl: './quote-card.component.html',
})
export class QuoteCardComponent implements OnInit {
  protected controller: CoverpathControllerInterface;

  // client properties
  clientName: string;
  insuredName: string;

  // quote properties
  frequencyUnit: string;
  shouldShowPremium: boolean = true;
  showPremiumToClient: boolean = true;

  // new shared values
  totalCoverage: number;
  totalPremium: number;
  artDescription: string;
  showFaceAmountBreakdown: boolean = false;

  // term values
  hasTermPolicy: boolean = false;
  termPremium: number;
  termFaceAmount: number;
  termHasWOP: boolean;
  termWOPValue: number;
  termHasECP: boolean;
  isART: boolean = false;
  term: number;
  totalTermPremium: number;

  // whole values
  hasWholeLifePolicy: boolean = false;
  wholeFaceAmount: number;
  wholeTerm: string;
  riders: QuoteRider[] | PolicyRider[];
  hasRtr: boolean = false;
  rtrAmount: number = 0;
  hasAlir: boolean = false;
  alirAmount: number = 0;
  hasExchange1035: boolean = false;
  cashTransferValue: number = 0;
  applyTowards: string = null;
  exchange1035Premium: number = 0;
  exchange1035TransferAmount: number = 0;
  totalWholePremium: number = null;
  showWholeLifeVisualization: boolean = false;
  insuredInfo: SafeInviteInfo;

  //
  greeting: string = `Your financial professional started your application for life insurance.`;
  premiumLabel: string = `Estimated Premium`;
  alertIntent = AlertIntent;
  skipTlic: boolean;
  hasRegistered: boolean;
  nbDecommissionPost30Days: boolean;

  constructor(
    protected amorphicService: AmorphicService,
    protected quickQuoteService: QuickQuoteService,
    private quoteCardService: QuoteCardService,
    private authenticationService: AuthenticationService
  ) {
    this.controller = amorphicService.controller;
  }

  async ngOnInit() {
    // Retrieve customer and quotes
    const insuredInfo = await (this.controller as Controller).inviteController.getSafeInviteInfo();
    this.insuredInfo = insuredInfo;

    // Get properties
    this.clientName =
      Utils.toCapitalizedWords(insuredInfo.policyOwnerEmailName) ||
      Utils.toCapitalizedWords(insuredInfo.primaryCustomer.firstName);
    const insuredFullName = Utils.toCapitalizedWords(insuredInfo.safePolicyInfo.primaryPolicy.insured.fullName);
    this.insuredName =
      insuredFullName.length > 15 ? Utils.toCapitalizedWords(insuredInfo.primaryCustomer.firstName) : insuredFullName;
    this.frequencyUnit = TimeFrequency[insuredInfo.safePolicyInfo.paymentFrequency.toLowerCase()];
    const quote = this.quickQuoteService.getCurrentAgentQuote() as CoverpathTermQuote;
    this.showPremiumToClient = quote.showPremiumToClient;

    if (insuredInfo.isAgentSubmittedApp) {
      this.greeting = `Congratulations! Your application for life insurance is approved.`;
      this.premiumLabel = `Premium`;
    }

    this.hasRegistered = insuredInfo.hasRegistered;

    // Get WholeLife policy info
    const safePolicy = insuredInfo.safePolicyInfo;
    this.hasWholeLifePolicy = safePolicy.hasWhole;
    if (safePolicy.hasWhole) {
      const wholeInfo = safePolicy.wholeInfo;
      this.showWholeLifeVisualization = !insuredInfo.isJointApp;
      this.wholeFaceAmount = wholeInfo.faceAmount; // wholeLifePolicy.faceAmountWithPendingAmendment();
      this.wholeTerm = wholeInfo.term.toString(); // wholeLifePolicy.term.toString();
      this.hasRtr = wholeInfo.hasRTR;
      this.rtrAmount = this.hasRtr ? wholeInfo.rtr.amount : 0; //wholeLifePolicy.rtrRider.amount : 0;
      this.hasAlir = wholeInfo.hasALIR; //wholeLifePolicy.hasAlirRider && wholeLifePolicy.hasAlirScheduledPayments;
      this.alirAmount = this.hasAlir ? this.quoteCardService.getAlirAmountAtIssue() : 0;
      this.hasExchange1035 = wholeInfo.hasExchange1035;
      this.cashTransferValue = wholeInfo.exchange1035 ? wholeInfo.exchange1035.cashValueTransfer : 0;
      this.applyTowards = wholeInfo.exchange1035 ? wholeInfo.exchange1035.applyTowards : null;
      this.totalWholePremium = wholeInfo.totalPremium;
      this.exchange1035Premium =
        this.cashTransferValue >= this.totalWholePremium ? this.totalWholePremium : this.cashTransferValue;
    }

    this.hasTermPolicy = safePolicy.hasTerm;
    if (safePolicy.hasTerm) {
      const termInfo = safePolicy.termInfo;
      this.termFaceAmount = termInfo.faceAmount;
      this.termPremium = termInfo.base;
      this.termHasWOP = termInfo.hasWOP;
      this.termHasECP = termInfo.hasECP;
      this.termWOPValue = termInfo.WOPValue;
      this.term = termInfo.term;
      this.isART = termInfo.isART;
      this.artDescription = Product.products.CASTerm.artDescription;
      this.totalTermPremium = termInfo.totalPremium;
    }

    this.showFaceAmountBreakdown = insuredInfo.isJointApp || this.hasRtr || this.hasAlir || this.hasExchange1035;

    this.totalCoverage = safePolicy.totalFaceAmount + this.rtrAmount + this.alirAmount;
    this.totalPremium = safePolicy.totalPremiumAmount;
    this.shouldShowPremium = this.showPremiumToClient && this.totalPremium >= 0;

    // Don't show skip TLIC disclaimer if it is an agent submitted app
    this.skipTlic = insuredInfo.skipTlic && !insuredInfo.isAgentSubmittedApp;
    this.nbDecommissionPost30Days = this.controller.nbDecommissionPost30Days;
  }

  /**
   * Begins application using Quick Quote Service
   */
  applyWithQuote(): void {
    if (this.hasRegistered) {
      this.authenticationService.goToLogin();
    } else {
      (this.controller as Controller).inviteController.reprocessInvite(true);
      this.quickQuoteService.setCustomerQuote();
      this.quickQuoteService.routeCustomer();
    }
  }

  scrollToQuickQuote(): void {
    CoverpathUtils.scrollToElement('quick-quote');
  }

  hasExchange1035TowardsPremium(): boolean {
    if (this.hasWholeLifePolicy) {
      return this.hasExchange1035 && this.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR ? true : false;
    } else {
      return false;
    }
  }

  exchange1035FundsExceedPremium(): boolean {
    let exchange1035TransferAmount: number = 0;
    if (this.hasExchange1035TowardsPremium()) {
      exchange1035TransferAmount =
        this.cashTransferValue > this.exchange1035Premium ? this.cashTransferValue - this.exchange1035Premium : 0;
    }
    return exchange1035TransferAmount > 0 ? true : false;
  }
}
